import { useEffect, useRef } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./BiometricDashboard.module.css";
import { GAUGE_CIRCUM, TriColorGauge } from "./TriColorGauge.tsx";
import type { GaugeSegments } from "./TriColorGauge.tsx";
import {
  ACCUMULATION_TIERS,
  AREA_PERF,
  DASH_HR,
  DASH_HR_AREA,
  DASH_HR_PATH,
  GW,
  HR_ZONE_BANDS,
  HR_ZONE_FG,
  INSIGHT_GAUGES,
  PAD,
  SESSION_CARDS,
  SVG_H,
  SVG_W,
  TAG_META,
  TIMELINE_MEMOS,
  TIMELINE_SHOTS,
  ZONE_META,
  buildCosinePath,
  dHrY,
  dTimeX,
} from "./dashboardData.ts";

/* ── Helpers ── */

const HR_ZONE_COLORS = ["#34c759", "#34c759", "#ffcc00", "#ff3b30"];
const HR_ZONE_LABELS = ["Z2", "Z3", "Z4", "Z5"];

/** Convert a single "OK%" value to GaugeSegments for TriColorGauge */
function valueToSegments(value: number): GaugeSegments {
  const remaining = 100 - value;
  const ext = Math.round(remaining * 0.2);
  const warn = Math.round(remaining * 0.3);
  return {
    ext1: ext,
    warn1: warn,
    ok: value,
    warn2: warn,
    ext2: remaining - ext - warn - warn,
  };
}

/* ── GSAP Desktop Animation: Phase 1–4 (pinned) ── */

function setupDesktopAnimation(el: HTMLElement, s: Record<string, string>) {
  const hrLine = el.querySelector(`.${s.hrLine}`) as SVGPathElement | null;
  const hrArea = el.querySelector(`.${s.hrArea}`) as SVGPathElement | null;
  const memoBubbles = el.querySelectorAll("[data-memo-bubble]");
  const bundleVisual = el.querySelector("[data-bundle]");
  const insightCards = el.querySelectorAll("[data-insight-card]");

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: el,
      pin: true,
      scrub: 1,
      start: "top top",
      end: "+=600%",
    },
  });

  /* ── Phase 1: Header ── */
  const header = el.querySelector(`.${s.header}`);
  if (header) {
    gsap.set(header, { y: 30, opacity: 0 });
    tl.to(header, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" });
  }

  /* Cosine background light */
  const cosPath = el.querySelector("[data-cosine-path]") as SVGPathElement | null;
  const cosGlow = el.querySelector("[data-cosine-glow]") as SVGPathElement | null;
  if (cosPath) {
    const len = cosPath.getTotalLength();
    gsap.set(cosPath, { strokeDasharray: len, strokeDashoffset: len });
    tl.to(cosPath, { strokeDashoffset: 0, duration: 14, ease: "none" }, 0);
  }
  if (cosGlow) {
    const len = cosGlow.getTotalLength();
    gsap.set(cosGlow, { strokeDasharray: len, strokeDashoffset: len, opacity: 0 });
    tl.to(cosGlow, { opacity: 0.4, duration: 1 }, 1);
    tl.to(cosGlow, { strokeDashoffset: 0, duration: 14, ease: "none" }, 0);
  }

  /* ── Phase 2: Synchronized timeline — HR + shots + memos ── */
  const drawDuration = 4;
  tl.addLabel("draw", 0.8);

  if (hrLine) {
    const len = hrLine.getTotalLength();
    gsap.set(hrLine, { strokeDasharray: len, strokeDashoffset: len });
    tl.to(hrLine, { strokeDashoffset: 0, duration: drawDuration, ease: "none" }, "draw");
  }
  if (hrArea) {
    gsap.set(hrArea, { opacity: 0 });
    tl.to(hrArea, { opacity: 0.2, duration: drawDuration * 0.7, ease: "power2.out" }, "draw+=0.5");
  }

  for (const shot of TIMELINE_SHOTS) {
    const progress = shot.timeIdx / (DASH_HR.length - 1);
    const dot = el.querySelector(`[data-shot-id="${shot.id}"]`);
    if (dot) {
      const cx = dTimeX(shot.timeIdx);
      const cy = dHrY(DASH_HR[shot.timeIdx]);
      gsap.set(dot, { scale: 0, opacity: 0, svgOrigin: `${cx} ${cy}` });
      tl.to(
        dot,
        { scale: 1, opacity: 1, duration: 0.2, ease: "back.out(2)" },
        `draw+=${progress * drawDuration}`,
      );
    }
  }

  if (memoBubbles.length > 0) {
    gsap.set(memoBubbles, { y: 20, opacity: 0, scale: 0.9 });
  }
  for (const memo of TIMELINE_MEMOS) {
    const progress = memo.timeIdx / (DASH_HR.length - 1);
    const bubble = el.querySelector(`[data-memo-id="${memo.lineIdx}"]`);
    if (bubble) {
      tl.to(
        bubble,
        { y: 0, opacity: 1, scale: 1, duration: 0.35, ease: "power3.out" },
        `draw+=${progress * drawDuration}`,
      );
      const tags = bubble.querySelectorAll("[data-memo-tag]");
      gsap.set(tags, { scale: 0 });
      tl.to(
        tags,
        { scale: 1, duration: 0.2, stagger: 0.06, ease: "back.out(3)" },
        `draw+=${progress * drawDuration + 0.18}`,
      );
    }
  }

  /* ── Phase 3: Memos stack → Session Summary Card ── */
  tl.addLabel("bundle", `draw+=${drawDuration + 0.5}`);

  const memoGrid = el.querySelector(`.${s.memoList}`) as HTMLElement | null;
  if (memoGrid && memoBubbles.length > 0) {
    const cards = Array.from(memoBubbles) as HTMLElement[];
    const gridW = memoGrid.offsetWidth;
    const gridH = memoGrid.offsetHeight;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const dx = gridW / 2 - card.offsetLeft - card.offsetWidth / 2;
      const dy = gridH / 2 - card.offsetTop - card.offsetHeight / 2;
      const rotation = (i - (cards.length - 1) / 2) * 6;
      const yShift = (i - (cards.length - 1) / 2) * 6;
      const cardOpacity = 0.5 + (i / (cards.length - 1)) * 0.5;

      tl.to(
        card,
        {
          x: dx,
          y: dy + yShift,
          scale: 0.65,
          rotation,
          opacity: cardOpacity,
          zIndex: i,
          duration: 1.2,
          ease: "power3.inOut",
        },
        `bundle+=${i * 0.06}`,
      );
    }
  }

  if (bundleVisual) {
    gsap.set(bundleVisual, { scale: 0.5, opacity: 0 });
    tl.to(
      bundleVisual,
      { scale: 1, opacity: 1, duration: 0.7, ease: "back.out(1.5)" },
      "bundle+=1.0",
    );

    const countEl = bundleVisual.querySelector("[data-bundle-count]") as HTMLElement | null;
    if (countEl) {
      const counter = { val: 0 };
      tl.to(
        counter,
        {
          val: 500,
          duration: 1.5,
          ease: "power2.out",
          snap: { val: 1 },
          onUpdate: () => {
            countEl.textContent = String(Math.round(counter.val));
          },
        },
        "bundle+=1.0",
      );
    }
  }

  /* ── Phase 4: Insight cards fan out ── */
  tl.addLabel("insights", "bundle+=1.4");

  if (insightCards.length > 0) {
    const rotations = [-6, 0, 6, -3, 3];
    let cardIdx = 0;
    for (const card of insightCards) {
      gsap.set(card, {
        y: 60,
        opacity: 0,
        rotation: rotations[cardIdx % rotations.length],
        scale: 0.85,
      });
      tl.to(
        card,
        { y: 0, opacity: 1, rotation: 0, scale: 1, duration: 0.7, ease: "power3.out" },
        `insights+=${cardIdx * 0.18}`,
      );

      const gaugeSegs = card.querySelectorAll("[data-gauge-seg]");
      let segIdx = 0;
      for (const seg of gaugeSegs) {
        const segEl = seg as SVGCircleElement;
        const targetLen = Number(segEl.dataset.gaugeSeg) || 0;
        const offset = Number(segEl.dataset.gaugeOffset) || 0;
        gsap.set(segEl, { strokeDasharray: `0 ${GAUGE_CIRCUM}`, strokeDashoffset: -offset });
        tl.to(
          segEl,
          {
            strokeDasharray: `${targetLen} ${GAUGE_CIRCUM - targetLen}`,
            duration: 0.6,
            ease: "power2.out",
          },
          `insights+=${cardIdx * 0.18 + 0.2 + segIdx * 0.12}`,
        );
        segIdx++;
      }

      const okText = card.querySelector("[data-gauge-ok]") as SVGTextElement | null;
      if (okText) {
        const target = Number(okText.dataset.gaugeOk) || 0;
        const obj = { val: 0 };
        tl.to(
          obj,
          {
            val: target,
            duration: 1,
            ease: "power2.out",
            onUpdate: () => {
              okText.textContent = `${Math.round(obj.val)}%`;
            },
          },
          `insights+=${cardIdx * 0.18 + 0.3}`,
        );
      }

      const bars = card.querySelectorAll("[data-bar-fill]");
      for (const bar of bars) {
        const target = (bar as HTMLElement).dataset.barFill || "0";
        gsap.set(bar, { width: "0%" });
        tl.to(
          bar,
          { width: `${target}%`, duration: 0.8, ease: "power2.out" },
          `insights+=${cardIdx * 0.18 + 0.3}`,
        );
      }

      cardIdx++;
    }
  }

  /* No Phase 5 — accumulation is handled outside the pin */
}

/* ── Component ── */

export function BiometricDashboard() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const cosinePath = buildCosinePath(1200, 600);
  const accRef = useRef<HTMLElement>(null);

  /* ── Pinned section (Phase 1–4) ── */
  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      if (isMobile) {
        const panels = el.querySelectorAll(
          `.${styles.timelinePanel}, .${styles.memoSection}, .${styles.insightGrid}`,
        );
        gsap.set(panels, { y: 30, opacity: 0 });
        gsap.to(panels, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.15,
          scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none reverse" },
        });
        return;
      }

      setupDesktopAnimation(el, styles);
    },
    [isMobile],
  );

  /* ── Accumulation section (post-pin, normal scroll) ── */
  useEffect(() => {
    const accEl = accRef.current;
    if (!accEl || reducedMotion) return;

    const ctx = gsap.context(() => {
      const blocks = accEl.querySelectorAll("[data-tier-block]");

      for (const block of blocks) {
        const blockEl = block as HTMLElement;

        /* Block entrance */
        gsap.from(blockEl, {
          y: 80,
          opacity: 0,
          scale: 0.9,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: blockEl,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        });

        /* Input cards stagger */
        const inputCards = blockEl.querySelectorAll("[data-tier-input]");
        if (inputCards.length > 0) {
          gsap.from(inputCards, {
            scale: 0.5,
            opacity: 0,
            y: 30,
            duration: 0.5,
            stagger: 0.04,
            ease: "back.out(1.5)",
            scrollTrigger: {
              trigger: blockEl,
              start: "top 75%",
              toggleActions: "play none none reverse",
            },
          });
        }

        /* Summary card pop */
        const summary = blockEl.querySelector("[data-tier-summary]");
        if (summary) {
          gsap.from(summary, {
            scale: 0.6,
            opacity: 0,
            duration: 0.8,
            ease: "back.out(1.5)",
            scrollTrigger: {
              trigger: summary,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          });
        }

        /* Gauge animations */
        const gaugeSegs = blockEl.querySelectorAll("[data-gauge-seg]");
        for (const seg of gaugeSegs) {
          const segEl = seg as SVGCircleElement;
          const targetLen = Number(segEl.dataset.gaugeSeg) || 0;
          const offset = Number(segEl.dataset.gaugeOffset) || 0;
          gsap.set(segEl, { strokeDasharray: `0 ${GAUGE_CIRCUM}`, strokeDashoffset: -offset });
          gsap.to(segEl, {
            strokeDasharray: `${targetLen} ${GAUGE_CIRCUM - targetLen}`,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: blockEl,
              start: "top 70%",
              toggleActions: "play none none reverse",
            },
          });
        }

        /* Gauge OK% count-up */
        const okTexts = blockEl.querySelectorAll("[data-gauge-ok]");
        for (const okText of okTexts) {
          const target = Number((okText as SVGTextElement).dataset.gaugeOk) || 0;
          const obj = { val: 0 };
          gsap.to(obj, {
            val: target,
            duration: 1.2,
            ease: "power2.out",
            onUpdate: () => {
              okText.textContent = `${Math.round(obj.val)}%`;
            },
            scrollTrigger: {
              trigger: blockEl,
              start: "top 70%",
              toggleActions: "play none none reverse",
            },
          });
        }

        /* HR zone bar fill */
        const hrBars = blockEl.querySelectorAll("[data-hr-fill]");
        for (const bar of hrBars) {
          const target = (bar as HTMLElement).dataset.hrFill || "0";
          gsap.set(bar, { width: "0%" });
          gsap.to(bar, {
            width: `${target}%`,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: blockEl,
              start: "top 70%",
              toggleActions: "play none none reverse",
            },
          });
        }
      }
    }, accEl);

    return () => ctx.revert();
  }, [reducedMotion]);

  const gl = t.ai.gaugeLabels;
  const gaugeLabels = [
    { label: t.ai.dashboardInsights[0].label, left: gl.direction.left, right: gl.direction.right },
    { label: t.ai.dashboardInsights[1].label, left: gl.arc.left, right: gl.arc.right },
    { label: t.ai.dashboardInsights[2].label, left: gl.depth.left, right: gl.depth.right },
  ];

  return (
    <>
      {/* ═══════════════ Pinned Section: Phase 1–4 ═══════════════ */}
      <section ref={ref} className={styles.section}>
        {/* Cosine Background Light */}
        <div className={styles.cosineBackground}>
          <svg
            viewBox="0 0 1200 600"
            preserveAspectRatio="none"
            className={styles.cosineSvg}
            role="img"
            aria-label="Background light effect"
          >
            <title>Background light</title>
            <defs>
              <linearGradient id="cosGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="rgba(110, 62, 255, 0.6)" />
                <stop offset="50%" stopColor="rgba(0, 113, 227, 0.5)" />
                <stop offset="100%" stopColor="rgba(52, 199, 89, 0.4)" />
              </linearGradient>
              <linearGradient id="cosGlowGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="rgba(110, 62, 255, 0.3)" />
                <stop offset="100%" stopColor="rgba(0, 113, 227, 0.2)" />
              </linearGradient>
            </defs>
            <path
              d={cosinePath}
              className={styles.cosineGlow}
              stroke="url(#cosGlowGrad)"
              data-cosine-glow
            />
            <path
              d={cosinePath}
              className={styles.cosinePath}
              stroke="url(#cosGrad)"
              data-cosine-path
            />
          </svg>
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t.ai.dashboardTitle}</h2>
            <p className={styles.subtitle}>{t.ai.dashboardDesc}</p>
          </div>

          {/* Timeline: HR Curve + Shot Dots */}
          <div className={styles.timelinePanel}>
            <div className={styles.timelineLabels}>
              <span className={styles.labelShots}>{t.ai.dashboardShots}</span>
              <span className={styles.labelSep}>+</span>
              <span className={styles.labelHr}>{t.ai.dashboardHeartRate}</span>
            </div>
            <div className={styles.svgWrap}>
              <svg
                viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Combined heart rate and shot timeline"
              >
                <title>HR + Shot Timeline</title>
                <defs>
                  <linearGradient id="dashHrGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3b30" />
                    <stop offset="100%" stopColor="transparent" />
                  </linearGradient>
                </defs>
                {HR_ZONE_BANDS.map((z) => (
                  <rect
                    key={z.min}
                    x={PAD.l}
                    y={dHrY(z.max)}
                    width={GW}
                    height={dHrY(z.min) - dHrY(z.max)}
                    fill={z.color}
                  />
                ))}
                {[100, 140, 180].map((bpm) => (
                  <text
                    key={bpm}
                    x={PAD.l - 4}
                    y={dHrY(bpm) + 3}
                    textAnchor="end"
                    className={styles.bpmLabel}
                  >
                    {bpm}
                  </text>
                ))}
                <path d={DASH_HR_AREA} className={styles.hrArea} />
                <path d={DASH_HR_PATH} className={styles.hrLine} />
                {TIMELINE_SHOTS.map((shot) => {
                  const cx = dTimeX(shot.timeIdx);
                  const cy = dHrY(DASH_HR[shot.timeIdx]);
                  const zm = ZONE_META[shot.zone];
                  return (
                    <g key={shot.id} data-shot-id={shot.id}>
                      {shot.made && (
                        <circle cx={cx} cy={cy} r={9} fill={`${zm.color}18`} stroke="none" />
                      )}
                      <circle
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={shot.made ? zm.color : "none"}
                        stroke={shot.made ? "none" : "#666"}
                        strokeWidth={shot.made ? 0 : 1.5}
                      />
                      <text
                        x={cx}
                        y={cy + 16}
                        textAnchor="middle"
                        className={styles.shotZoneText}
                        fill={zm.color}
                      >
                        {zm.letter}
                      </text>
                      {shot.error && (
                        <text
                          x={cx}
                          y={cy + 25}
                          textAnchor="middle"
                          className={styles.shotErrorText}
                        >
                          {shot.error}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Voice Memos */}
          <div className={styles.memoSection}>
            <div className={styles.memoColumnLabel}>{t.ai.dashboardVoiceMemos}</div>
            <div className={styles.memoListWrap}>
              <div className={styles.memoList}>
                {TIMELINE_MEMOS.map((memo) => (
                  <div
                    key={`memo-${memo.lineIdx}`}
                    className={styles.memoBubble}
                    data-memo-bubble
                    data-memo-id={memo.lineIdx}
                  >
                    <p className={styles.memoText}>{t.ai.dashboardVoiceLines[memo.lineIdx]}</p>
                    <div className={styles.memoTags}>
                      {memo.tags.map((tag) => {
                        const meta = TAG_META[tag.key];
                        return (
                          <span
                            key={tag.key}
                            className={styles.memoTag}
                            data-memo-tag
                            style={{
                              backgroundColor: `${meta.color}1a`,
                              color: meta.color,
                              borderColor: `${meta.color}33`,
                            }}
                          >
                            {meta.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {/* Session Summary Card */}
              <div className={styles.sessionCard} data-bundle>
                <div className={styles.sessionCardInner}>
                  <span className={styles.sessionCardLabel}>Session #1</span>
                  <div className={styles.sessionCardStats}>
                    <span className={styles.sessionCardShots} data-bundle-count>
                      0
                    </span>
                    <span className={styles.sessionCardUnit}>{t.ai.dashboardShotsUnit}</span>
                  </div>
                  <span className={styles.sessionCardDuration}>
                    {t.ai.dashboardSessionDuration}
                  </span>
                  <span className={styles.sessionCardFg}>FG 54%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Insight Cards */}
          <div className={styles.insightGrid}>
            {INSIGHT_GAUGES.map((gauge, gIdx) => (
              <div
                key={gauge.id}
                className={`${styles.insightCard} ${styles.gaugeCard}`}
                data-insight-card
              >
                <div className={styles.insightCardHeader}>
                  <span className={styles.insightLabel}>{t.ai.dashboardInsights[gIdx].label}</span>
                  <span
                    className={`${styles.insightTrend} ${gauge.trendType === "positive" ? styles.insightTrendUp : styles.insightTrendDown}`}
                  >
                    {gauge.trend}
                  </span>
                </div>
                <div className={styles.gaugeWrap}>
                  <TriColorGauge
                    segments={gauge.segments}
                    label={t.ai.dashboardInsights[gIdx].label}
                    leftLabel={gaugeLabels[gIdx].left}
                    rightLabel={gaugeLabels[gIdx].right}
                  />
                </div>
                <p className={styles.insightValue}>{t.ai.dashboardInsights[gIdx].value}</p>
              </div>
            ))}
            <div
              className={`${styles.insightCard} ${styles.wideCard}`}
              style={{ "--card-accent": "#ff3b30" } as React.CSSProperties}
              data-insight-card
            >
              <div className={styles.insightCardHeader}>
                <span className={styles.insightIcon}>{"\u2665"}</span>
                <span className={styles.insightLabel}>{t.ai.dashboardInsights[3].label}</span>
              </div>
              <div className={styles.hrZoneBars}>
                {HR_ZONE_FG.map((z) => (
                  <div key={z.zone} className={styles.hrZoneRow}>
                    <span className={styles.hrZoneLabel}>{z.zone}</span>
                    <span className={styles.hrZoneBpm}>{z.bpm}</span>
                    <div className={styles.hrZoneTrack}>
                      <div
                        className={styles.hrZoneFill}
                        data-bar-fill={z.pct}
                        style={{ background: z.color }}
                      />
                    </div>
                    <span className={styles.hrZonePct} style={{ color: z.color }}>
                      {z.pct}%
                    </span>
                  </div>
                ))}
              </div>
              <p className={styles.insightValue}>{t.ai.dashboardInsights[3].value}</p>
            </div>
            <div
              className={`${styles.insightCard} ${styles.wideCard}`}
              style={{ "--card-accent": "#34c759" } as React.CSSProperties}
              data-insight-card
            >
              <div className={styles.insightCardHeader}>
                <span className={styles.insightIcon}>{"\u25CE"}</span>
                <span className={styles.insightLabel}>{t.ai.dashboardInsights[4].label}</span>
              </div>
              <div className={styles.areaPerfTable}>
                <div className={styles.areaPerfHeader}>
                  <span className={styles.areaPerfHdArea}>Area</span>
                  <span className={styles.areaPerfHdFg}>FG%</span>
                  <span className={styles.areaPerfHdHr}>Avg HR</span>
                </div>
                {AREA_PERF.map((a) => (
                  <div key={a.area} className={styles.areaPerfRow}>
                    <span className={styles.areaPerfLabel}>{a.area}</span>
                    <div className={styles.areaPerfBarWrap}>
                      <div
                        className={styles.areaPerfBar}
                        data-bar-fill={a.fg}
                        style={{ background: a.barColor }}
                      />
                    </div>
                    <span className={styles.areaPerfFg}>{a.fg}%</span>
                    <span className={styles.areaPerfHr} style={{ color: a.hrColor }}>
                      {a.avgHr} BPM
                    </span>
                  </div>
                ))}
              </div>
              <p className={styles.insightValue}>{t.ai.dashboardInsights[4].value}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ Accumulation Section: Post-Pin Scroll ═══════════════ */}
      <section ref={accRef} className={styles.accSection}>
        {ACCUMULATION_TIERS.map((tier, tIdx) => {
          const gaugeKeys = ["direction", "arc", "depth"] as const;

          return (
            <div
              key={tier.id}
              className={styles.tierBlock}
              data-tier-block
              data-tier-scale={tier.cardScale}
              style={{ "--tier-scale": tier.cardScale } as React.CSSProperties}
            >
              {/* Input cards that converge into this tier */}
              <div
                className={`${styles.tierInputGrid} ${(tIdx === 0 ? SESSION_CARDS : tier.cards).length > 5 ? styles.tierInputGridWrap : ""}`}
              >
                {tIdx === 0
                  ? SESSION_CARDS.map((s) => (
                      <div key={s.id} className={styles.tierInputCard} data-tier-input>
                        <span className={styles.tierInputNum}>#{s.num}</span>
                        <span className={styles.tierInputShots}>{s.shots}</span>
                        <span className={styles.tierInputFg}>FG {s.fg}%</span>
                      </div>
                    ))
                  : tier.cards.map((card) => (
                      <div key={card.label} className={styles.tierInputCard} data-tier-input>
                        <span className={styles.tierInputNum}>{card.label}</span>
                        <span className={styles.tierInputShots}>{card.sub}</span>
                        <span className={styles.tierInputFg}>FG {card.fg}%</span>
                      </div>
                    ))}
              </div>

              {/* Converge arrow */}
              <div className={styles.tierArrow}>
                <span className={styles.tierArrowIcon}>{"\u25BC"}</span>
              </div>

              {/* Summary card */}
              <div className={styles.tierSummaryCard} data-tier-summary>
                <div className={styles.tierSummaryHeader}>
                  <span className={styles.tierSummaryLabel}>{tier.summaryLabel}</span>
                  <span className={styles.tierSummaryBig}>{tier.summaryCount}</span>
                  <span className={styles.tierSummaryUnit}>{tier.summaryUnit}</span>
                  <span className={styles.tierSummaryFg}>FG {tier.summaryFg}%</span>
                </div>

                {/* Direction / Arc / Depth gauges */}
                <div className={styles.tierGaugeGrid}>
                  {gaugeKeys.map((gk, gi) => {
                    const g = tier.gauges[gk];
                    const segs = valueToSegments(g.value);
                    const meta = gaugeLabels[gi];
                    const isPositive = g.trend.startsWith("+");
                    return (
                      <div key={gk} className={styles.tierGaugeCard}>
                        <div className={styles.tierGaugeHeader}>
                          <span className={styles.tierGaugeLabel}>{meta.label}</span>
                          <span
                            className={`${styles.tierGaugeTrend} ${isPositive ? styles.trendUp : styles.trendDown}`}
                          >
                            {g.trend}
                          </span>
                        </div>
                        <div className={styles.tierGaugeWrap}>
                          <TriColorGauge
                            segments={segs}
                            label={meta.label}
                            leftLabel={meta.left}
                            rightLabel={meta.right}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* HR zone bars */}
                <div className={styles.tierHrSection}>
                  <span className={styles.tierHrTitle}>{"\u2665"} HR Zone FG%</span>
                  <div className={styles.tierHrBars}>
                    {tier.hrZones.map((pct, zi) => (
                      <div key={HR_ZONE_LABELS[zi]} className={styles.tierHrRow}>
                        <span className={styles.tierHrLabel} style={{ color: HR_ZONE_COLORS[zi] }}>
                          {HR_ZONE_LABELS[zi]}
                        </span>
                        <div className={styles.tierHrTrack}>
                          <div
                            className={styles.tierHrFill}
                            data-hr-fill={pct}
                            style={{ background: HR_ZONE_COLORS[zi] }}
                          />
                        </div>
                        <span className={styles.tierHrPct} style={{ color: HR_ZONE_COLORS[zi] }}>
                          {pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
}
